import { Bill } from '@enview/interface/types/bills/Bill';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import BillDetailedCustomFieldModal from './BillDetailedCustomFieldModal';
import MemoIcon from '../Icons/MemoIcon';
import { CustomFieldsAPI } from '../../../api';
import { useSelector } from 'react-redux';
import { findTeamFieldTitlesWithValues } from '../../../helpers/BillHelper';
import { State } from '../../../dux/@types';
import './BillCustomField.scss';
import { Organization } from '@enview/interface/types/Organization';

interface BillDetailedCustomFieldProps {
  bill: Bill;
  organization: Organization;
}

export const BillDetailedCustomField: React.FC<BillDetailedCustomFieldProps> = ({
  bill,
  organization,
}) => {
  const [showModal, setShowModal] = useState(false);

  const togglePopover = () => {
    setShowModal(!showModal);
  };

  const { data: customFields = [] } =
    CustomFieldsAPI.endpoints.getOrganizationCustomFields.useQuery(organization.id);

  const teamId = useSelector((state: State) => state.teamMode.teamId);

  const fieldTitlesWithValue = useMemo(
    () =>
      findTeamFieldTitlesWithValues(
        bill.customFields ?? {},
        organization.id,
        teamId,
        customFields,
      ),
    [customFields, teamId, bill.customFields],
  );

  const [activeField, setActiveField] = useState<string | undefined>(
    fieldTitlesWithValue[0],
  );

  useEffect(() => {
    setActiveField((prev) => {
      if (!fieldTitlesWithValue.length) {
        return undefined;
      }

      if (!prev || !fieldTitlesWithValue.includes(prev)) {
        return fieldTitlesWithValue[0];
      }

      return prev;
    });
  }, [fieldTitlesWithValue]);

  const hasValueSet = !!activeField && fieldTitlesWithValue.includes(activeField);

  const targetElement: ReactElement = (
    <div className={'cf-button ml-2'} id={`cf-button-${bill.id}`} title="">
      <MemoIcon color={hasValueSet ? '#F79728' : '#919191'} size={24} />
      <p className="field-placeholder ml-2">
        {activeField ? activeField : 'Add a bill property'}
      </p>
    </div>
  );

  return (
    <div
      className="ml-2"
      onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
      style={{ lineHeight: '20px', marginTop: '6px', width: 'fit-content' }}
    >
      <BillDetailedCustomFieldModal
        activeField={activeField}
        bill={bill}
        closePopup={() => setShowModal(false)}
        customFields={customFields}
        positions={['bottom', 'right']}
        setActiveField={setActiveField}
        show={showModal}
        target={targetElement}
        toggleCallback={togglePopover}
      />
    </div>
  );
};

export default BillDetailedCustomField;
